import { useEffect, useState } from "react";
import { Plus, FileText, Trash2, Lock, Link2 } from "lucide-react";
import { api, TASK_TYPES, TASK_TYPE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FormBuilder from "@/components/FormBuilder";
import FormRenderer from "@/components/FormRenderer";
import { toast } from "sonner";

const CATS = [
  { v: "training", l: "Training" },
  { v: "performance", l: "Performance" },
  { v: "development", l: "Development" },
  { v: "document", l: "Document" },
];
const NONE = "__none__";

export default function Templates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isAdmin = user.role === "admin";

  const load = () => api.get("/templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const del = async (e, id) => { e.stopPropagation(); await api.delete(`/templates/${id}`); toast.success("Template deleted"); load(); };
  const openTpl = (t) => { setEditing(t); setOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Document Templates</h1>
          <p className="mt-1 text-sm text-slate-500">Click a template to open it. Templates linked to a work type auto-attach to that work.</p>
        </div>
        {isAdmin ? (
          <Button data-testid="add-template-button" onClick={() => { setEditing(null); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="mr-1.5 h-4 w-4" /> New template
          </Button>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-slate-400"><Lock className="h-3.5 w-3.5" /> Only admins can edit templates</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => (
          <div key={t.id} data-testid="template-card" onClick={() => openTpl(t)} className="cursor-pointer rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-50 text-orange-600"><FileText className="h-4 w-4" /></div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-600">{t.category}</span>
            </div>
            <p className="mt-3 font-heading font-semibold text-slate-900">{t.name}</p>
            <p className="text-sm text-slate-500">{t.description}</p>
            {t.task_type && <p className="mt-2 flex items-center gap-1 text-xs font-medium text-orange-600"><Link2 className="h-3 w-3" /> {TASK_TYPE_LABEL[t.task_type] || t.task_type}</p>}
            {isAdmin && (
              <button onClick={(e) => del(e, t.id)} className="mt-3 flex items-center gap-1 text-xs text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
            )}
          </div>
        ))}
        {templates.length === 0 && <p className="text-slate-400">No templates yet.</p>}
      </div>

      <TemplateDialog open={open} onOpenChange={setOpen} editing={editing} readOnly={!isAdmin} onSaved={load} />
    </div>
  );
}

function TemplateDialog({ open, onOpenChange, editing, readOnly, onSaved }) {
  const [form, setForm] = useState({ name: "", category: "performance", description: "", task_type: NONE, fields: [] });
  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? { name: editing.name, category: editing.category, description: editing.description || "", task_type: editing.task_type || NONE, fields: editing.fields || [] }
      : { name: "", category: "performance", description: "", task_type: NONE, fields: [] });
  }, [open, editing]);

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const payload = { ...form, body: "", task_type: form.task_type === NONE ? null : form.task_type };
    if (editing) await api.put(`/templates/${editing.id}`, payload);
    else await api.post("/templates", payload);
    toast.success(editing ? "Template updated" : "Template created");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto esp-scroll" data-testid="template-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? (readOnly ? "Template" : "Edit template") : "New template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Name</Label>
            <Input className="mt-1.5" data-testid="template-name-input" disabled={readOnly} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))} disabled={readOnly}>
                <SelectTrigger className="mt-1.5" data-testid="template-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Linked work type</Label>
              <Select value={form.task_type} onValueChange={(v) => setForm((f) => ({ ...f, task_type: v }))} disabled={readOnly}>
                <SelectTrigger className="mt-1.5" data-testid="template-tasktype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {TASK_TYPES.filter((t) => t.v !== "general").map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Description</Label>
            <Input className="mt-1.5" disabled={readOnly} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Questions</Label>
            <p className="mb-2 text-xs text-slate-400">Build the form people will fill in — add questions and choose an answer type.</p>
            {readOnly
              ? <FormRenderer fields={form.fields} answers={{}} readOnly />
              : <FormBuilder fields={form.fields} onChange={(fields) => setForm((f) => ({ ...f, fields }))} />}
          </div>
          {!readOnly && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="template-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600">{editing ? "Save" : "Create"}</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
